<template>
  <div>
    <!-- Shown only when there is something to say: an empty card would push the timeline down for
         nothing. -->
    <NeedsAttentionCardComponent
      v-if="pools.attentionRows.length > 0 || pools.attentionError !== null"
      class="mb-3"
      :rows="pools.attentionRows"
      :error="pools.attentionError"
      :description="$t('forwards.pools.attention.description')"
      @retry="pools.loadAttention"
    />

    <div
      v-if="pools.hasNoPools"
      class="pools-intro border rounded-3 p-4 d-flex align-items-start gap-3"
      data-testid="pools-intro"
    >
      <FontAwesomeIcon
        icon="fa-solid fa-calendar-alt"
        class="text-primary fs-3 flex-shrink-0"
      />
      <div>
        <h5 class="mb-2">{{ $t("forwards.pools.intro.title") }}</h5>
        <ForwardSalesIntroComponent :country class="mb-3" />
        <button
          type="button"
          class="btn btn-primary"
          data-testid="create-first-pool"
          @click="poolEditor.open(null)"
        >
          <FontAwesomeIcon icon="fa-solid fa-plus" class="me-1" />
          {{ $t("forwards.pools.intro.cta") }}
        </button>
      </div>
    </div>

    <template v-else>
      <div class="d-flex align-items-end flex-wrap gap-3 mb-3">
        <TimelineYearStepperComponent v-model="pools.year" />

        <div class="pools-filter">
          <label
            class="eyebrow-label eyebrow-label--sm mb-1"
            :for="statusFilterId"
            >{{ $t("forwards.pools.status_filter") }}</label
          >
          <MultiSelectComponent
            v-model="pools.statuses"
            :options="statusOptions"
            :buttonId="statusFilterId"
            :placeholder="$t('forwards.pools.status_filter_all')"
            size="sm"
            hideBulkActions
          />
        </div>

        <div class="pools-filter">
          <label
            class="eyebrow-label eyebrow-label--sm mb-1"
            :for="nameFilterId"
            >{{ $t("forwards.pools.name_filter") }}</label
          >
          <input
            :id="nameFilterId"
            v-model="pools.nameFilter"
            type="search"
            class="form-control form-control-sm"
            :placeholder="$t('forwards.pools.name_filter_placeholder')"
          />
        </div>

        <div class="pools-filter">
          <label
            class="eyebrow-label eyebrow-label--sm mb-1"
            :for="companyFilterId"
            >{{ $t("forwards.pools.company_filter") }}</label
          >
          <MultiSelectComponent
            v-model="pools.companyIds"
            :options="companyOptions"
            :buttonId="companyFilterId"
            :placeholder="$t('forwards.pools.company_filter_all')"
            size="sm"
          />
        </div>

        <ul
          class="pools-legend list-unstyled d-flex align-items-center gap-3 mb-0 ms-auto"
        >
          <li class="d-flex align-items-center gap-1">
            <span class="forwards-swatch forwards-swatch--escrowed"></span
            >{{ $t("forwards.common.escrowed") }}
          </li>
          <li class="d-flex align-items-center gap-1">
            <span class="forwards-swatch forwards-swatch--committed"></span
            >{{ $t("forwards.common.committed") }}
          </li>
          <li class="d-flex align-items-center gap-1">
            <span class="forwards-swatch forwards-swatch--available"></span>
            <InfoTooltip :text="$t('forwards.pools.available_help')">{{
              $t("forwards.common.available")
            }}</InfoTooltip>
          </li>
        </ul>
      </div>

      <div v-if="errorMessage" class="error-note mb-3" role="alert">
        <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
        <span>{{ errorMessage }}</span>
        <button
          type="button"
          class="btn btn-sm btn-link ms-auto p-0"
          @click="pools.load()"
        >
          {{ $t("forwards.common.retry") }}
        </button>
      </div>

      <div class="border rounded-3 p-3">
        <LoadingIndicatorComponent
          v-if="pools.loading"
          :message="$t('forwards.pools.loading')"
        />
        <p
          v-else-if="pools.isEmpty"
          class="text-body-tertiary text-center py-4 mb-0"
        >
          {{ $t("forwards.pools.empty") }}
        </p>
        <template v-else-if="pools.timeline">
          <!-- The band stays the whole scope's total: it is titled "All pools", and the name filter
               only narrows which rows are on screen. -->
          <PoolTimelineBandComponent
            v-if="pools.rows.length > 0"
            :band="pools.band"
            :windowQuarters="pools.windowQuarters"
            :bandNote="pools.timeline.band_note"
          />
          <p
            v-if="pools.noNameMatches"
            class="text-body-tertiary text-center py-4 mb-0"
          >
            {{ $t("forwards.pools.name_filter_no_matches") }}
          </p>
          <PoolTimelineRowComponent
            v-for="row in pools.filteredRows"
            :key="row.pool_id"
            :row="row"
            :windowQuarters="pools.windowQuarters"
          />
          <div
            v-if="pools.filteredRestrictedPools.length > 0"
            class="restricted-pools mt-3"
          >
            <div
              v-for="pool in pools.filteredRestrictedPools"
              :key="pool.pool_id"
              class="restricted-pool d-flex align-items-start gap-2"
            >
              <FontAwesomeIcon
                icon="fa-solid fa-lock"
                class="text-body-tertiary mt-1"
              />
              <div>
                <div class="fw-semibold">{{ pool.name }}</div>
                <div class="small text-body-tertiary">
                  {{ $t("forwards.pools.restricted_access") }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
      <!-- Not while loading: an account without pools would see it flash before the introduction,
           which holds the same materials. -->
      <ForwardSalesGuideComponent v-if="!pools.loading" :country class="mt-3" />
    </template>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import ForwardSalesGuideComponent from "@/forwards2/components/ForwardSalesGuideComponent.vue";
import ForwardSalesIntroComponent from "@/forwards2/components/ForwardSalesIntroComponent.vue";
import NeedsAttentionCardComponent from "@/forwards2/overview/NeedsAttentionCardComponent.vue";
import { forwardsErrorMessage } from "@/forwards2/forwardsApi";
import { POOL_STATUS_FILTERS } from "@/forwards2/forwardsTypes";
import TimelineYearStepperComponent from "@/forwards2/pools/TimelineYearStepperComponent.vue";
import PoolTimelineBandComponent from "@/forwards2/pools/PoolTimelineBandComponent.vue";
import PoolTimelineRowComponent from "@/forwards2/pools/PoolTimelineRowComponent.vue";
import { usePoolEditorLauncher } from "@/forwards2/pools/usePoolEditorLauncher";
import { usePoolsTimeline } from "@/forwards2/pools/usePoolsTimeline";
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The pools in the dashboard's scope over one calendar year: their Needs attention rows, the timeline
 * with its filters, and the collapsible forward sales guide below it. An account that has never had a
 * pool gets an introduction to forward sales, with the forward sales materials open and a button to
 * create its first pool, instead of the filters, an empty timeline and the guide; filters that merely hide every pool still show the timeline's empty state. A pool's name opens the editor modal the forward
 * card renders; the card calls `reload` after an editor session that wrote.
 *
 * `companyIds` seeds the company filter once, at setup: the open company in a single-company view,
 * none in the consolidated one, which shows every pool in the account's scope. The user may clear or
 * change it from there. Mount the panel per seed — a dashboard switching companies has to remount
 * it, or it keeps the previous company's filter.
 */
const props = withDefaults(
  defineProps<{
    companyIds: number[];
    /** Picks the materials' language versions (`ForwardSalesMaterialsComponent`). */
    country?: string | null;
  }>(),
  { country: null }
);

const { t } = useI18n();
const pools = usePoolsTimeline(props.companyIds);
const poolEditor = usePoolEditorLauncher();

const statusFilterId = useId();
const companyFilterId = useId();
const nameFilterId = useId();

const statusOptions = computed(() =>
  POOL_STATUS_FILTERS.map((value) => ({
    value,
    label: t(`forwards.pools.status.${value}`),
  }))
);

const companyOptions = computed(() =>
  pools.companies.map((company) => ({
    value: company.company_id,
    label: company.name,
  }))
);

const errorMessage = computed(() =>
  pools.error === null
    ? null
    : (forwardsErrorMessage(pools.error) ?? t("forwards.pools.load_failed"))
);

defineExpose({ reload: pools.reload });
</script>

<style scoped lang="scss">
@use "@/forwards2/forwards-colors" as colors;

.pools-filter {
  min-width: 13rem;
}

.pools-legend {
  font-size: 0.75rem;
  color: var(--phoenix-gray-600);
}

.restricted-pools {
  border-top: 1px solid var(--phoenix-border-color-translucent);
}

.restricted-pool {
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--phoenix-border-color-translucent);
}

.forwards-swatch {
  display: inline-block;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 2px;
  flex-shrink: 0;
}

.forwards-swatch--escrowed {
  background: colors.$forwards-escrowed;
}

.forwards-swatch--committed {
  background: colors.$forwards-committed;
}

.forwards-swatch--available {
  background: colors.$forwards-available;
}
</style>
